import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  useEffect(() => {
    document.title = comic ? `${comic.title} - 奇妙漫画网` : '漫画未找到 - 奇妙漫画网';
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-book-2-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">漫画未找到</h2>
            <p className="text-foreground-500 mb-6">抱歉，找不到该漫画信息</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    {
      label: `${comic.category}漫画`,
      href: `/category/${categories.find((c) => c.name === comic.category)?.slug || 'romance'}`,
    },
    { label: comic.title },
  ];

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画详情`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2025-01-01',
    dateModified: comic.updateDate,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: item.label,
        ...(item.href ? { item: item.href } : {}),
      })),
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main>
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-end pt-16">
          <div className="absolute inset-0 bg-foreground-950"></div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 pb-10 pt-20">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mt-6 flex flex-col lg:flex-row gap-8 lg:gap-16">
                <div className="flex-shrink-0 w-48 md:w-56">
                  <div className="rounded-xl overflow-hidden aspect-[5/7] bg-background-100">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} 漫画封面`}
                      title={`${comic.title} - 好看的韩国漫画`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h1 className="text-2xl md:text-4xl font-black text-background-50 font-heading mb-3">
                    {comic.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 md:gap-5 text-sm text-background-300/80 mb-6">
                    <span>
                      <i className="ri-user-line mr-1"></i>
                      作者：{comic.author}
                    </span>
                    <span>
                      <i className="ri-bookmark-line mr-1"></i>
                      状态：{comic.status}
                    </span>
                    <span>
                      <i className="ri-star-fill text-accent-500 mr-1"></i>
                      {comic.rating}
                    </span>
                    <span>
                      <i className="ri-file-list-3-line mr-1"></i>
                      共{comic.chapters}话
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="whitespace-nowrap px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      开始阅读
                    </button>
                    <button
                      type="button"
                      className="whitespace-nowrap px-4 py-2.5 border border-background-400/30 text-background-300 text-sm font-medium rounded-md hover:bg-background-50/10 transition-colors cursor-pointer"
                    >
                      <i className="ri-heart-line mr-1.5"></i>
                      收藏
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 lg:px-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-20">
                <div className="w-full lg:w-[40%]">
                  <span className="text-xs font-medium text-primary-500 tracking-wider">
                    （作品信息）
                  </span>
                </div>
                <div className="w-full lg:w-[60%]">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
                    剧情介绍
                  </h2>
                  <article className="prose-sm max-w-none">
                    <p className="text-base text-foreground-600 leading-relaxed">
                      {comic.description}
                    </p>
                  </article>

                  <div className="grid grid-cols-2 gap-4 mt-10">
                    <div className="rounded-xl overflow-hidden aspect-[16/10] bg-background-200">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} 内页预览`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="rounded-xl overflow-hidden aspect-[16/10] bg-background-200">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} 精彩画面`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-14 md:py-20 bg-background-50">
            <div className="px-4 md:px-6 lg:px-10">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-8">
                  同类<span className="text-primary-500">推荐</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                  {relatedComics.map((rc) => (
                    <ComicCard
                      key={rc.id}
                      id={rc.id}
                      title={rc.title}
                      category={rc.category}
                      cover={rc.cover}
                      rating={rc.rating}
                      hot={rc.hot}
                      status={rc.status}
                      chapters={rc.chapters}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}